import { AgentTask } from '../../../services/api/aiAgentApi/aiAgent';
import { describeTaskAction } from '../lib/agentDisplay';

import { EDITABLE_FIELDS_BY_TOOL } from './constants';

export interface TaskEditValues {
  subject?: string;
  body?: string;
}

export function asTaskArgsRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === `object`) {
    return value as Record<string, unknown>;
  }

  return {};
}

export function asTaskArgString(value: unknown): string | undefined {
  if (typeof value !== `string`) {
    return undefined;
  }

  return value.trim();
}

export function asTaskArgStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item): item is string => typeof item === `string`)
    .map(item => item.trim())
    .filter(Boolean);
}

export function readResolvedTaskRecipientEmails(args: Record<string, unknown>): string | undefined {
  const resolvedRecipientEmails = asTaskArgStringArray(args._resolvedRecipientEmails);

  if (resolvedRecipientEmails.length) {
    return resolvedRecipientEmails.join(`, `);
  }

  return asTaskArgString(args.overrideEmail);
}

export function getEditableFieldsForTool(toolName: string): Array<`subject` | `body`> {
  return EDITABLE_FIELDS_BY_TOOL[toolName] || [];
}

export function getInitialTaskEditValues(task: AgentTask): TaskEditValues {
  const editableFields = getEditableFieldsForTool(task.tool_name);
  const args = asTaskArgsRecord(task.args);
  const subject = asTaskArgString(args.subject);
  const body = asTaskArgString(args.body);

  if (!editableFields.length) {
    return {};
  }

  if (task.tool_name === `send_statement`) {
    return {
      subject: subject || ``,
      body: body || ``,
    };
  }

  return {
    subject: editableFields.includes(`subject`) ? subject || `` : undefined,
    body: editableFields.includes(`body`) ? body || `` : undefined,
  };
}

export function getPendingTaskReadOnlyDetail(task: AgentTask): string | undefined {
  const args = asTaskArgsRecord(task.args);

  switch (task.tool_name) {
  case `send_email_to_customer`: {
    const toValue = readResolvedTaskRecipientEmails(args) || `Default contact email`;

    return `To: ${toValue}`;
  }
  case `send_sms_to_customer`:
    return `To: ${asTaskArgString(args.overrideNumber) || `Default contact number`}`;
  case `resend_invoice_email`: {
    const toValue = readResolvedTaskRecipientEmails(args) || `Selected contact email on file`;

    return `To: ${toValue}`;
  }
  case `send_statement`:
    return undefined;
  default:
    return describeTaskAction(task).detail;
  }
}

export function buildEditedArgsForTask(task: AgentTask, taskEdit: TaskEditValues): TaskEditValues | undefined {
  const editableFields = getEditableFieldsForTool(task.tool_name);
  const originalArgs = asTaskArgsRecord(task.original_args ?? task.args);
  const editedArgs: TaskEditValues = {};

  editableFields.forEach(field => {
    const rawValue = taskEdit[field];
    const nextValue = typeof rawValue === `string` ? rawValue.trim() : ``;

    if (!nextValue) {
      return;
    }

    const originalValue = asTaskArgString(originalArgs[field]) || ``;

    if (nextValue !== originalValue) {
      editedArgs[field] = nextValue;
    }
  });

  return Object.keys(editedArgs).length ? editedArgs : undefined;
}
